/*
 * One animation frame loop and one timer, shared by every canvas that draws on them, so a row of
 * moving cards costs one `requestAnimationFrame` chain and one `setInterval` however many it holds.
 * Each stops once nothing is registered.
 */

/** A drawing, given the frame's time in ms (`performance.now()`'s clock). */
type Draw = (now: number) => void;

interface FrameSubscriber {
  draw: Draw;
  interval: number;
  last: number;
}

const frameSubscribers = new Set<FrameSubscriber>();
let frame = 0;

/** One frame: every drawing whose interval has passed. Stops once nothing is registered. */
function tick(now: number): void {
  frame = 0;
  for (const subscriber of frameSubscribers) {
    if (now - subscriber.last < subscriber.interval) continue;
    subscriber.last = now;
    subscriber.draw(now);
  }
  if (frameSubscribers.size > 0) frame = requestAnimationFrame(tick);
}

/** Calls `draw` at most every `interval` ms on the browser's frames, until the returned stop. */
export function onFrames(draw: Draw, interval: number): () => void {
  const subscriber: FrameSubscriber = { draw, interval, last: Number.NEGATIVE_INFINITY };
  frameSubscribers.add(subscriber);
  if (frame === 0) frame = requestAnimationFrame(tick);
  return () => {
    frameSubscribers.delete(subscriber);
    if (frameSubscribers.size === 0 && frame !== 0) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  };
}

/** How often a registered check runs, in ms. */
const CHECK_MS = 500;
const checks = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;

/** Calls `check` twice a second, until the returned stop (one timer for every check). */
export function onChecks(check: () => void): () => void {
  checks.add(check);
  timer ??= setInterval(() => {
    for (const each of checks) each();
  }, CHECK_MS);
  return () => {
    checks.delete(check);
    if (checks.size === 0 && timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}
